import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, Pencil, Search, X, Check, UserPlus, IdCard, Info } from "lucide-react";
import PortalShell from "@/components/PortalShell";
import { portalApi, portalMe } from "@/lib/portalApi";

const TYPE_OPTIONS = [
  { id: "single_guy", label: "Single Guy" },
  { id: "single_lady", label: "Single Lady" },
  { id: "couple", label: "Couple" },
  { id: "tv", label: "TV" },
];
const TYPE_LABEL = Object.fromEntries(TYPE_OPTIONS.map((t) => [t.id, t.label]));

export default function PortalMembers() {
  const [me, setMe] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [meRes, mRes] = await Promise.all([portalMe(), portalApi.get("/portal/members")]);
      setMe(meRes);
      setMembers(mRes.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const enabled = !!me?.venue?.memberships_enabled;

  const upload = async (f) => {
    if (!f || uploading) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await portalApi.post("/portal/members/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.inserted}${data.skipped ? ` · ${data.skipped} skipped` : ""}.`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed.");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this member?")) return;
    try {
      await portalApi.delete(`/portal/members/${id}`);
      setMembers((ms) => ms.filter((m) => m.id !== id));
      toast.success("Member removed.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const filtered = members.filter((m) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [m.email, m.first_name, m.last_name, m.phone, m.membership_number].some(
      (v) => (v || "").toLowerCase().includes(q)
    );
  });

  return (
    <PortalShell me={me} testId="portal-members-page">
      <div className="max-w-[1300px] mx-auto px-6 md:px-8 py-10">
        <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.28em] text-[#D9A6F5] mb-3">Digital memberships</div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tighter">
              {loading ? "Loading…" : `${members.length} member${members.length === 1 ? "" : "s"}`}
            </h1>
            <p className="mt-3 text-sm text-[#A3A3A3] max-w-xl">
              Upload your existing list, add new members manually, or edit anyone in-line.
            </p>
          </div>
          {enabled && (
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                data-testid="members-upload"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-2 border border-white/20 hover:border-[#D9A6F5] hover:text-[#D9A6F5] px-5 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors disabled:opacity-60"
              >
                {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                {uploading ? "Uploading…" : "Upload CSV"}
              </button>
              <button
                type="button"
                data-testid="members-add-open"
                onClick={() => { setShowAdd(true); setEditingId(null); }}
                className="inline-flex items-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] px-6 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
              >
                <UserPlus size={13} /> Add member
              </button>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                data-testid="members-file"
                onChange={(e) => upload(e.target.files?.[0] || null)}
              />
            </div>
          )}
        </div>

        {!enabled && (
          <div className="border border-white/10 bg-[#0A0A0A] p-6 mb-6 flex items-start gap-3" data-testid="members-disabled-banner">
            <Info size={16} className="text-[#D9A6F5] shrink-0 mt-0.5" />
            <div className="text-sm text-[#A3A3A3]">
              Digital memberships are turned off for your venue. Enable them in{" "}
              <a href="/portal/venue" className="text-[#D9A6F5] hover:underline">Update Venue Info</a> to start managing members.
            </div>
          </div>
        )}

        {enabled && (
          <>
            <div className="relative mb-4">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
              <input
                type="text"
                placeholder="Search by name, email, phone or membership number"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="members-search"
                className="w-full bg-black border border-white/15 h-11 pl-10 pr-3 text-[#F5F5F0] text-sm focus:outline-none focus:border-[#D9A6F5]"
              />
            </div>

            {showAdd && (
              <MemberForm
                mode="create"
                onCancel={() => setShowAdd(false)}
                onSaved={async () => { setShowAdd(false); await load(); toast.success("Member added."); }}
              />
            )}

            {loading ? (
              <div className="py-20 flex items-center justify-center">
                <Loader2 size={20} className="animate-spin text-[#D9A6F5]" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-20 text-[#A3A3A3]">
                {members.length === 0
                  ? "No members yet. Upload a CSV or add one manually."
                  : "No members match your search."}
              </div>
            ) : (
              <div className="bg-[#0A0A0A] border border-white/10">
                <div className="hidden md:grid grid-cols-[130px_1.4fr_1.6fr_1fr_1fr_100px_60px] gap-4 px-5 py-3 border-b border-white/10 text-[10px] uppercase tracking-[0.22em] text-[#D9A6F5]">
                  <div>Member #</div>
                  <div>Name</div>
                  <div>Email</div>
                  <div>Phone</div>
                  <div>Type</div>
                  <div></div>
                  <div></div>
                </div>
                {filtered.map((m) =>
                  editingId === m.id ? (
                    <MemberForm
                      key={m.id}
                      mode="edit"
                      member={m}
                      onCancel={() => setEditingId(null)}
                      onSaved={async () => { setEditingId(null); await load(); toast.success("Member updated."); }}
                    />
                  ) : (
                    <div
                      key={m.id}
                      data-testid={`member-row-${m.id}`}
                      className="grid md:grid-cols-[130px_1.4fr_1.6fr_1fr_1fr_100px_60px] grid-cols-1 gap-2 md:gap-4 px-5 py-3 border-b border-white/5 last:border-b-0 text-sm items-center"
                    >
                      <div className="inline-flex items-center gap-2 text-[#F5F5F0] font-mono text-xs">
                        <IdCard size={12} className="text-[#D9A6F5]" /> {m.membership_number}
                      </div>
                      <div className="text-[#F5F5F0] truncate">
                        {(m.first_name || "") + " " + (m.last_name || "") || "—"}
                      </div>
                      <div className="text-[#A3A3A3] truncate">{m.email}</div>
                      <div className="text-[#A3A3A3] truncate">{m.phone || "—"}</div>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[#D9A6F5]">
                        {TYPE_LABEL[m.membership_type] || m.membership_type}
                      </div>
                      <div className="text-right">
                        <button
                          type="button"
                          data-testid={`member-edit-${m.id}`}
                          onClick={() => { setEditingId(m.id); setShowAdd(false); }}
                          className="inline-flex items-center gap-1 text-[#A3A3A3] hover:text-[#D9A6F5] text-[10px] uppercase tracking-[0.2em] font-bold"
                        >
                          <Pencil size={11} /> Edit
                        </button>
                      </div>
                      <div className="text-right">
                        <button
                          type="button"
                          onClick={() => remove(m.id)}
                          data-testid={`member-delete-${m.id}`}
                          className="text-[#A3A3A3] hover:text-red-400 p-1"
                          aria-label="Remove"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  )
                )}
              </div>
            )}
          </>
        )}
      </div>
    </PortalShell>
  );
}

function MemberForm({ mode, member, onCancel, onSaved }) {
  const [f, setF] = useState({
    membership_number: member?.membership_number || "",
    first_name: member?.first_name || "",
    last_name: member?.last_name || "",
    email: member?.email || "",
    phone: member?.phone || "",
    membership_type: member?.membership_type || "couple",
  });
  const [saving, setSaving] = useState(false);
  const isEdit = mode === "edit";

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        membership_number: f.membership_number.trim(),
        first_name: f.first_name.trim(),
        last_name: f.last_name.trim() || null,
        email: f.email.trim().toLowerCase(),
        phone: f.phone.trim() || null,
        membership_type: f.membership_type,
      };
      if (isEdit) {
        await portalApi.patch(`/portal/members/${member.id}`, payload);
      } else {
        await portalApi.post("/portal/members", payload);
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={save}
      data-testid={isEdit ? `member-edit-form-${member.id}` : "member-create-form"}
      className={`grid grid-cols-1 md:grid-cols-[130px_1.4fr_1.6fr_1fr_1fr_100px_60px] gap-2 md:gap-3 px-5 py-4 ${
        isEdit ? "border-b border-white/10 bg-[#111]" : "border border-white/15 bg-[#0A0A0A] mb-4"
      }`}
    >
      <input required type="text" placeholder="Member #" value={f.membership_number}
        onChange={(e) => setF({ ...f, membership_number: e.target.value })}
        data-testid="member-form-number"
        className="bg-black border border-white/15 h-10 px-3 text-[#F5F5F0] text-sm focus:outline-none focus:border-[#D9A6F5] font-mono" />
      <div className="grid grid-cols-2 gap-2">
        <input required type="text" placeholder="First" value={f.first_name}
          onChange={(e) => setF({ ...f, first_name: e.target.value })}
          data-testid="member-form-first-name"
          className="bg-black border border-white/15 h-10 px-3 text-[#F5F5F0] text-sm focus:outline-none focus:border-[#D9A6F5]" />
        <input type="text" placeholder="Surname" value={f.last_name}
          onChange={(e) => setF({ ...f, last_name: e.target.value })}
          data-testid="member-form-last-name"
          className="bg-black border border-white/15 h-10 px-3 text-[#F5F5F0] text-sm focus:outline-none focus:border-[#D9A6F5]" />
      </div>
      <input required type="email" placeholder="email@example.com" value={f.email}
        onChange={(e) => setF({ ...f, email: e.target.value })}
        data-testid="member-form-email"
        className="bg-black border border-white/15 h-10 px-3 text-[#F5F5F0] text-sm focus:outline-none focus:border-[#D9A6F5]" />
      <input type="tel" placeholder="Phone" value={f.phone}
        onChange={(e) => setF({ ...f, phone: e.target.value })}
        data-testid="member-form-phone"
        className="bg-black border border-white/15 h-10 px-3 text-[#F5F5F0] text-sm focus:outline-none focus:border-[#D9A6F5]" />
      <select value={f.membership_type}
        onChange={(e) => setF({ ...f, membership_type: e.target.value })}
        data-testid="member-form-type"
        className="bg-black border border-white/15 h-10 px-3 text-[#F5F5F0] text-sm focus:outline-none focus:border-[#D9A6F5]">
        {TYPE_OPTIONS.map((t) => (
          <option key={t.id} value={t.id}>{t.label}</option>
        ))}
      </select>
      <button type="submit" disabled={saving}
        data-testid="member-form-save"
        className="inline-flex items-center justify-center gap-1 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] disabled:opacity-60 h-10 text-[10px] uppercase tracking-[0.22em] font-bold">
        {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save
      </button>
      <button type="button" onClick={onCancel}
        data-testid="member-form-cancel"
        className="inline-flex items-center justify-center gap-1 border border-white/15 hover:border-white/40 h-10 text-[10px] uppercase tracking-[0.22em] font-bold text-[#A3A3A3] hover:text-[#F5F5F0]">
        <X size={12} /> Cancel
      </button>
    </form>
  );
}
